// client/src/components/StudentForm.jsx
// Single form for create AND edit of a Student.
//
// Error handling: server-side zod issues (422 with details[]) map to
// inline field errors by `path`. Coded business errors (409 conflicts +
// 422 invalid_card_number) map to specific Hebrew copy on the offending
// field. Any other error renders in a sticky top banner that scrolls
// into view automatically so the user can't miss it.
//
// Card number normalization handles plain digits, spaces, dashes, and
// Track-2 magnetic-stripe pastes (";<18>=<18>?") via parseCardInput.
//
// Dirty tracking: the parent gets a callback on every transition so it
// can pass isDirty to <Modal> for the unsaved-changes confirm.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../lib/api.js';
import { parseCardInput } from '../lib/card.js';
import { useClasses } from '../hooks/useClasses.js';
import { ExclamationTriangleIcon } from './Icons.jsx';

export default function StudentForm({
  campId,
  initial,
  onSubmit,
  onCancel,
  submitLabel,
  onDirtyChange,
}) {
  const [firstName, setFirstName] = useState(initial?.firstName || '');
  const [lastName, setLastName] = useState(initial?.lastName || '');
  const [nationalId, setNationalId] = useState(initial?.nationalId || '');
  const [className, setClassName] = useState(initial?.className || '');
  const [externalId, setExternalId] = useState(initial?.externalId || '');
  const [cardNumber, setCardNumber] = useState(initial?.cardNumber || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState(null);
  const topErrorRef = useRef(null);

  // Class dropdown — a student's class must reference a live class (or be
  // empty). We keep the student's current class selectable even if it was
  // archived/renamed away, so editing other fields never drops it silently.
  const classesQ = useClasses(campId);
  const classOptions = useMemo(() => {
    const names = (classesQ.data ?? []).map((c) => c.name);
    if (className && !names.includes(className)) names.unshift(className);
    return names;
  }, [classesQ.data, className]);

  // Dirty tracking — compare current values to initial. A successful save
  // re-mounts the form (parent closes the modal) so we don't need to
  // explicitly "reset original" on submit.
  const initialFlat = useMemo(
    () =>
      JSON.stringify({
        firstName: initial?.firstName || '',
        lastName: initial?.lastName || '',
        nationalId: initial?.nationalId || '',
        className: initial?.className || '',
        externalId: initial?.externalId || '',
        cardNumber: initial?.cardNumber || '',
        notes: initial?.notes || '',
      }),
    [initial]
  );
  const currentFlat = JSON.stringify({
    firstName,
    lastName,
    nationalId,
    className,
    externalId,
    cardNumber,
    notes,
  });
  const isDirty = currentFlat !== initialFlat;

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  // When a top-level error appears, scroll it into view — modal bodies
  // are scrollable and the user submits from the bottom button; without
  // this they'd never see the error message at the top.
  useEffect(() => {
    if (topError && topErrorRef.current) {
      topErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [topError]);

  function handleCancel() {
    if (isDirty) {
      const ok = window.confirm('יש שינויים שלא נשמרו.\nלבטל?');
      if (!ok) return;
    }
    onCancel();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});
    setTopError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        nationalId: nationalId.trim() || null,
        className: className.trim() || null,
        externalId: externalId.trim() || null,
        cardNumber: cardNumber.trim() || null,
        notes: notes.trim() || null,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && Array.isArray(err.details)) {
          const fieldErrors = {};
          for (const issue of err.details) fieldErrors[issue.path] = issue.message;
          setErrors(fieldErrors);
          setTopError('יש לתקן את השדות המסומנים ולנסות שוב.');
        } else if (err.code === 'conflict' && err.message === 'card_already_assigned') {
          setErrors({ cardNumber: 'הכרטיס הזה כבר משויך לתלמיד אחר' });
          setTopError('הכרטיס כבר רשום במערכת.');
        } else if (err.code === 'conflict' && err.message === 'external_id_taken') {
          setErrors({ externalId: 'המזהה החיצוני הזה כבר בשימוש' });
          setTopError('המזהה כבר רשום במערכת.');
        } else if (err.code === 'validation_failed' && err.message === 'invalid_card_number') {
          setErrors({ cardNumber: 'מספר הכרטיס אינו תקין — לא כרטיס נדרים פעיל' });
          setTopError('הכרטיס לא עובר אימות מול מסד הנתונים של נדרים פלוס.');
        } else if (err.code === 'validation_failed' && err.message === 'class_not_found') {
          setErrors({ className: 'הכיתה אינה קיימת — בחר כיתה מהרשימה' });
          setTopError('יש לבחור כיתה קיימת או להשאיר ריק.');
        } else if (err.status === 422) {
          // Generic 422 fallback — server rejected the input but didn't
          // give us a specific subcode. Show the message verbatim if
          // present; otherwise a friendly default.
          setTopError(
            err.message && err.message !== err.code
              ? `שגיאת ולידציה: ${err.message}`
              : 'אחד השדות לא תקין. בדוק את הערכים ונסה שוב.'
          );
        } else if (err.status === 401) {
          setTopError('הסשן פג. נסה לרענן את הדף ולהיכנס מחדש.');
        } else if (err.status === 403) {
          setTopError('אין לך הרשאה לפעולה זו.');
        } else if (err.status >= 500) {
          setTopError(`שגיאת שרת (${err.status}). נסה שוב בעוד רגע.`);
        } else {
          setTopError(`שגיאה: ${err.message || err.code || 'לא צפויה'}`);
        }
      } else {
        setTopError('שגיאה לא צפויה. בדוק שיש חיבור אינטרנט ונסה שוב.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {topError && (
        <div
          ref={topErrorRef}
          role="alert"
          className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2 animate-slide-up"
        >
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{topError}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="שם פרטי" error={errors.firstName} required>
          <input
            type="text"
            className="input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            maxLength={50}
            autoFocus
          />
        </Field>
        <Field label="שם משפחה" error={errors.lastName} required>
          <input
            type="text"
            className="input"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            maxLength={50}
          />
        </Field>
      </div>
      <Field label="מספר זהות" error={errors.nationalId} hint="תעודת זהות (לא חובה)">
        <input
          type="text"
          inputMode="numeric"
          className="input"
          value={nationalId}
          onChange={(e) => setNationalId(e.target.value)}
          maxLength={20}
          dir="ltr"
        />
      </Field>
      <Field
        label="כיתה"
        error={errors.className}
        hint="נבחר מתוך הכיתות שהוגדרו במסך הכיתות"
      >
        <select
          className="input"
          value={className}
          onChange={(e) => setClassName(e.target.value)}
        >
          <option value="">ללא כיתה</option>
          {classOptions.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="מזהה חיצוני"
        error={errors.externalId}
        hint="מזהה ממערכת בית הספר, אם רלוונטי"
      >
        <input
          type="text"
          className="input"
          value={externalId}
          onChange={(e) => setExternalId(e.target.value)}
          maxLength={50}
        />
      </Field>
      <Field
        label="מספר כרטיס נדרים"
        error={errors.cardNumber}
        hint="16 ספרות. ניתן להדביק עם רווחים או נתוני פס מגנטי."
      >
        <input
          type="text"
          inputMode="numeric"
          pattern="\d{16}"
          className="input"
          value={cardNumber}
          /* parseCardInput handles plain digits, spaces, dashes, AND
             track-2 mag-stripe pastes (";<18>=<18>?") — extracts the
             16-digit PAN via the Nedarim algorithm. */
          onChange={(e) => setCardNumber(parseCardInput(e.target.value))}
          dir="ltr"
        />
      </Field>
      <Field label="הערות" error={errors.notes}>
        <textarea
          className="input"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
        />
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-secondary" onClick={handleCancel}>
          ביטול
        </button>
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'שומר...' : submitLabel || 'שמור'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, error, hint, required, children }) {
  return (
    <label className="block">
      <span className="label">
        {label} {required && <span className="text-joy-600">*</span>}
      </span>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
